<template>
    <div class="mak-kontroller">
        <v-overlay absolute :value="loading">
            <v-progress-circular indeterminate size="64"/>
        </v-overlay>

        <v-row class="mx-2 my-6">
            <div class="headline" v-text="monthYearStr"/>

            <v-spacer></v-spacer>
            <v-progress-circular
                :rotate="360"
                :width="15"
                :size="100"
                :value="progress.ratio"
                :color="progress.color"
            >
                {{ progress.ratio }} %
            </v-progress-circular>
            <v-spacer></v-spacer>

            <v-btn-toggle>
                <v-btn
                    :disabled="prevMonthDisabled || loading"
                    @click="addMonth(-1)">
                    <v-icon left>
                        mdi-chevron-left
                    </v-icon>
                    Önceki Ay
                </v-btn>
                <v-btn
                    :disabled="nextMonthDisabled || loading"
                    @click="addMonth(1)">
                    Sonraki Ay
                    <v-icon right>
                        mdi-chevron-right
                    </v-icon>
                </v-btn>
            </v-btn-toggle>
        </v-row>

        <v-expansion-panels v-model="activeName" class="mak-kontroller-panel">
            <v-expansion-panel
                v-for="kontrol in gunlukKontroller"
                :key="kontrol.ayin_gunu">

                <v-expansion-panel-header>
                    <div>
                        <span v-if="kontrol.ayin_gunu == day && isThisMonth">Bugün</span>
                        <span v-else>{{monthStr}} <b>{{kontrol.ayin_gunu}}.</b> günü</span>

                        <v-chip
                            v-if="!kontrol.missing"
                            class="ml-6"
                            color="green"
                            text-color="white"
                        >
                            <v-icon left>
                                mdi-checkbox-marked-circle
                            </v-icon>
                            Hepsi Tamam
                        </v-chip>
                        <v-chip
                            v-else
                            class="ml-6"
                            color="orange"
                            text-color="orange"
                            outlined
                        >
                            <v-icon left>
                                mdi-alert
                            </v-icon>
                            <span v-html="kontrol.message"></span>
                        </v-chip>
                    </div>

                    <template v-slot:actions>
                        <v-icon color="deep-purple">
                            $expand
                        </v-icon>
                    </template>
                </v-expansion-panel-header>

                <v-expansion-panel-content>
                    <mak-musteri-kontrolleri
                        :key="`${year}-${month}-${kontrol.ayin_gunu}`"
                        :year="year"
                        :month="month"
                        :day="kontrol.ayin_gunu"
                        :toplam-kontrol="kontrol.toplamKontrol"
                        :tamamlanan-kontrol="kontrol.tamamlananKontrol"
                        @refresh="fetchTamamlananKontrolSayisi"
                    />
                </v-expansion-panel-content>

            </v-expansion-panel>
        </v-expansion-panels>
    </div>
</template>

<script>
    import moment from 'moment';
    import MakMusteriKontrolleri from '@/components/MakMusteriKontrolleri.vue'
    import Vuex from 'vuex';

    export default {
        name: 'mak-kontroller',
        components: { MakMusteriKontrolleri },
        data() {
            return {
                addDaysValue: 0,
                activeName: `${moment().date()}`,
                aylikToplam: [],
                aylikTamamlananToplam: [],
                processing: {
                    toplam: false,
                    tamamlanan: false,
                },
            };
        },
        computed: {
            ...Vuex.mapState(['user']),
            limit() {
                if (this.user.admin) {
                    return { min: -12, max: 1 };
                }
                return { min: -1, max: 1 };
            },
            loading() {
                const { toplam, tamamlanan } = this.processing;
                return toplam || tamamlanan;
            },
            isThisMonth() {
                return moment().format('YYYY') === this.year
                    && moment().format('MM') === this.month;
            },
            dateValue() {
                if (this.addDaysValue === 0) {
                    return moment();
                }
                return moment().add(this.addDaysValue, 'months');
            },
            monthStr() {
                return this.dateValue.format('MMMM');
            },
            monthYearStr() {
                return this.dateValue.format('MMMM YYYY');
            },
            year() {
                return this.dateValue.format('YYYY');
            },
            month() {
                return this.dateValue.format('MM');
            },
            day() {
                return this.dateValue.format('D');
            },
            nextMonthDisabled() {
                return this.addDaysValue === this.limit.max;
            },
            prevMonthDisabled() {
                return this.addDaysValue === this.limit.min;
            },
            params() {
                return {
                    year: this.year,
                    month: this.month,
                };
            },
            gunlukKontroller() {
                if (!this.aylikTamamlananToplam.length) {
                    return [];
                }
                return this.aylikTamamlananToplam.map((d) => {
                    d.toplamKontrol = this.toplamMapping[d.ayin_gunu];
                    d.tamamlananKontrol = d.kontrol_sayisi;
                    d.missing = d.toplamKontrol - d.tamamlananKontrol;
                    d.message = `<b>${d.missing}</b> kontrol eksik / <b>${d.toplamKontrol}</b> Toplam`;
                    return d;
                });
            },
            toplamMapping() {
                if (!this.aylikToplam.length) {
                    return {};
                }
                return this.aylikToplam.reduce((obj, d) => {
                    obj[d.ayin_gunu] = d.kontrol_sayisi;
                    return obj;
                }, {})
            },
            progress() {
                if (!this.gunlukKontroller.length) {
                    return {
                        size: 100,
                        value: 0,
                    };
                }
                const size = this.gunlukKontroller.reduce((res, k) => (res += k.toplamKontrol, res), 0);
                const value = this.gunlukKontroller.reduce((res, k) => (res += k.tamamlananKontrol, res), 0);
                const ratio = (size === 0 ? 0 : value / size * 100).toFixed(0);
                let color = 'success';
                if (ratio < 20) {
                    color = 'red';
                } else if (ratio < 40) {
                    color = 'orange';
                } else if (ratio < 60) {
                    color = 'primary';
                } else if (ratio < 80) {
                    color = 'teal';
                }
                return {
                    size,
                    value,
                    ratio,
                    color,
                };
            },
        },
        methods: {
            addMonth(val) {
                if (!val) {
                    this.addDaysValue = 0;
                    return;
                }

                if (val < 0) {
                    this.addDaysValue -= 1;
                    return;
                }
                this.addDaysValue += 1;

            },
            go(name) {
                this.$router.push({ name });
            },
            fetchKontrolSayisi() {
                this.processing.toplam = true;
                this.$requests
                    .MusteriKontrol.getKontrolAylikSayi(this.params)
                    .then((data) => {
                        this.aylikToplam = data
                            .map((d) => {
                                d.kontrol_sayisi = parseInt(d.kontrol_sayisi);
                                return d;
                            });
                    })
                    .catch((e) => {
                        console.error('error', e);
                    })
                    .then(() => {
                        this.processing.toplam = false;
                    });
            },
            fetchTamamlananKontrolSayisi() {
                this.processing.tamamlanan = true;
                this.$requests
                    .MusteriKontrol.getKontrolAylikTamamlananSayi(this.params)
                    .then((data) => {
                        this.aylikTamamlananToplam = data
                            .map((d) => {
                                d.kontrol_sayisi = parseInt(d.kontrol_sayisi);
                                return d;
                            })
                            .sort((a, b) => parseInt(a.ayin_gunu) - parseInt(b.ayin_gunu));
                    })
                    .catch((e) => {
                        console.error('error', e);
                    })
                    .then(() => {
                        this.processing.tamamlanan = false;
                    });
            },
        },
        mounted() {
            this.fetchKontrolSayisi();
            this.fetchTamamlananKontrolSayisi();
        },
        watch: {
            params() {
                this.fetchKontrolSayisi();
                this.fetchTamamlananKontrolSayisi();
                if (this.isThisMonth) {
                    this.activeName = `${moment().date()}`;
                } else {
                    this.activeName = null;
                }
            },
        },
    };
</script>

<style lang="scss">
    .mak-kontroller {
        .mak-kontrol-satiri {
            width: 100%;
            padding-right: 20px;
            font-size: 1.15rem;

            &.is-today {
                background-color: #00808020;
            }
            .el-tag {
                font-size: 1.05rem;
            }
        }

        .mak-month-selection {
            .el-button {
                line-height: 15px;
            }
        }

        .mak-kontroller-panel {
            .v-expansion-panel-content__wrap {
                padding: 0;
            }
        }
    }
</style>
